import { useEffect, useState } from 'react';

import { Badge } from '@playbooks/ui/components/badge';
import { Switch } from '@playbooks/ui/components/switch';

import {
	ComposerActionFeedback,
	ComposerActionHeader,
	ComposerActionListSkeleton,
	ComposerActionRow,
} from 'components/composer/composer-actions-components';
import { useProject, useStore, useWorkspace } from 'contexts';
import { useInit } from 'hooks';
import { FlowBody } from 'utils/flow';
import { projectResourcePayload } from 'utils/project-resources';

const categories = [
	{ key: 'web', title: 'Web tools' },
	{ key: 'maps', title: 'Map Tools' },
	{ key: 'automation', title: 'Automation tools' },
	{ key: 'workspace', title: 'Workspace tools' },
	{ key: 'project', title: 'Project tools' },
];

const ComposerActionsTools = ({
	type,
	disabled,
	canManagePreferences,
	canManageProjectDefaults,
	preferences: draftPreferences,
	onChange,
	onNext,
	onPrev,
}) => {
	const [tools, setTools] = useState<any[]>([]);
	const [workspaceTools, setWorkspaceTools] = useState<any[]>([]);
	const project = useProject();
	const store = useStore();
	const workspace = useWorkspace();
	const draftMode = type === 'app';
	const preferences = draftMode ? draftPreferences || {} : {};
	const explicitDraft = draftMode && Object.prototype.hasOwnProperty.call(preferences, 'tools');
	const selected = draftMode
		? explicitDraft
			? preferences.tools || []
			: workspaceTools.length
				? workspaceTools.filter(tool => tool.enabled && tool.scopes?.includes('project'))
				: tools.filter(tool => tool.defaultEnabled && tool.scopes?.includes('project'))
		: (project.project?.tools || []).map(item => item.tool);
	const rootApi = type === 'project' ? `${project.rootApi}/resources` : null;
	const isDisabled = tool => {
		if (disabled) return true;
		if (type === 'app') return false;
		if (type === 'workspace') return tool.group === 'project' ? !canManageProjectDefaults : !canManagePreferences;
		return !canManagePreferences;
	};

	const [fetchTools, loading, error] = useInit(async () => {
		const fetchWorkspace = ['workspace', 'app'].includes(type) && workspace.workspace?.id;
		const [catalog, workspaceCatalog] = await Promise.all([
			store.query({ url: '/agent-tools' }),
			fetchWorkspace ? store.query({ url: `${workspace.rootApi}/tools` }) : Promise.resolve({ data: null }),
		]);
		setTools(catalog.data || []);
		setWorkspaceTools(workspaceCatalog.data || []);
	});

	useEffect(() => {
		fetchTools();
	}, [store, type, workspace.rootApi, workspace.workspace?.id]);

	const onWorkspaceToolToggle = async tool => {
		if (isDisabled(tool) || type !== 'workspace') return;
		if (
			tool.key === 'workspace_read' &&
			tool.enabled &&
			!window.confirm(
				'Disable Workspace Read? Without Workspace Read, Playbooks cannot see or answer questions about this workspace.',
			)
		)
			return;
		if (
			tool.key === 'production_access' &&
			!tool.enabled &&
			!window.confirm('Enable Production Access? The agent may run commands and change live production data.')
		)
			return;
		const response = await store.updateRecord({
			method: 'PATCH',
			url: `${workspace.rootApi}/tools/${tool.key}`,
			data: { enabled: !tool.enabled },
		});
		setWorkspaceTools(values => values.map(value => (value.id === response.data.id ? response.data : value)));
		onNext?.();
	};

	const onProjectToggle = async tool => {
		if (isDisabled(tool)) return;
		const enabled = selected.some(item => item.id === tool.id);
		if (
			!enabled &&
			tool.key === 'production_access' &&
			!window.confirm('Enable Production Access? The agent may run commands and change live production data.')
		)
			return;
		const values = (enabled ? selected.filter(item => item.id !== tool.id) : [...selected, tool]).map(
			(item, position) => ({ id: item.id, position }),
		);
		if (draftMode) return onChange?.({ ...preferences, tools: values });
		const response = await store.updateRecord({
			url: rootApi,
			data: projectResourcePayload(project.project, { toolIds: values.map(item => item.id) }),
		});
		project.onUpdate(response.data);
		onNext?.();
	};

	const displayedTools = (
		type === 'workspace' ? workspaceTools : tools.filter(tool => tool.scopes?.includes('project'))
	).filter(tool => tool.key !== 'browser_access');
	const displayedCategories = categories.filter(category => type === 'workspace' || category.key !== 'workspace');

	return (
		<>
			<ComposerActionHeader title='Tools' onPrev={onPrev} />
			<FlowBody>
				{loading ? (
					<ComposerActionListSkeleton count={type === 'workspace' ? 10 : 4} />
				) : error ? (
					<ComposerActionFeedback
						icon='triangle-exclamation'
						title='Tools unavailable'
						text="We couldn't load the available agent tools."
					/>
				) : (
					<div className='grid gap-1 p-1'>
						{displayedCategories.map((category, categoryIndex) => {
							const items = displayedTools.filter(tool => tool.group === category.key);
							if (!items.length) return null;
							return (
								<div key={category.key}>
									{categoryIndex ? <div className='my-1 border-t border-border' /> : null}
									<p className='type-pretitle px-2 pb-1 pt-2 text-foreground-subtle'>{category.title}</p>
									{items.map(tool => {
										const unavailable = tool.status !== 'active';
										const rowDisabled = isDisabled(tool) || unavailable;
										const enabled =
											type === 'workspace' ? Boolean(tool.enabled) : selected.some(item => item.id === tool.id);
										return (
											<ComposerActionRow
												key={tool.id}
												disabled={rowDisabled}
												trailing={
													<Switch className='pointer-events-none' size='sm' checked={enabled} disabled={rowDisabled} />
												}
												onClick={() => (type === 'workspace' ? onWorkspaceToolToggle(tool) : onProjectToggle(tool))}>
												<span className='grid min-w-0 gap-0.5'>
													<span className='flex flex-wrap items-center gap-1.5 text-foreground'>
														{tool.name}
														{tool.key === 'workspace_read' ? <Badge variant='outline'>Confirm to disable</Badge> : null}
														{tool.key === 'production_access' ? (
															<Badge variant='outline'>Confirm to enable</Badge>
														) : null}
													</span>
													<span className='truncate text-xs font-normal text-foreground-subtle'>
														{tool.description}
													</span>
												</span>
											</ComposerActionRow>
										);
									})}
								</div>
							);
						})}
					</div>
				)}
			</FlowBody>
		</>
	);
};

export { ComposerActionsTools };
